/**
 * The bridge between the wire shape (generated camelCase types) and the shape
 * the live engagement surfaces render from.
 *
 * **Why there is no `fromMockScene()` (plan §2 divergence).** The plan proposed
 * a second entry point converting the prototype `EngagementScene` into this
 * same view-model so Storybook could drive the identical components. The two
 * shapes are non-isomorphic in both directions: the mock `Engagement` union
 * requires `bracket_distribution` and `terms_version`, which the wire
 * deliberately never exposes, while the wire carries `isSMBAccount` and the
 * server-computed hours totals the mock scene has no field for. (ENG-5604
 * closed the rest of that list — `created_by_user_id`, the scope fields,
 * `billing_cadence` and `start_date` are all on the wire now.) Writing the
 * adapter either way still means inventing values — exactly what the "never
 * hand-write snapshot fields" invariant forbids. So the bridge stays
 * one-directional: live data → this view-model → the live surfaces; the
 * prototype tree keeps rendering `EngagementScene` for Storybook and FE3–FE6.
 *
 * Money rules (plan §2 / §6.3):
 *   - Every amount is cents as a `number | null`. **null means the viewer is
 *     not entitled to it** — the resolvers deny by returning null, so render
 *     nothing. Never coerce a denied amount to 0; a `$0.00` where the server
 *     said "not yours" is the leak this whole module exists to prevent.
 *   - The view-model never *computes* customer money. Take rates are staff-
 *     gated and never reach the client, so `derivePricing` is impossible on
 *     live surfaces and the server snapshots are the only display source. The
 *     one surface that needs a figure no snapshot carries — the Hire drawer's
 *     price preview — asks the server for it (`engagementPricingPreview`)
 *     rather than deriving it here.
 */
import type {
  EngagementBillingCadence,
  EngagementBillingPeriodStatus,
  EngagementBillingQuery,
  EngagementBillingViewerSide,
  EngagementChannelType,
  EngagementCreationPath,
  EngagementFundingMethod,
  EngagementHubQuery,
  EngagementNextAction,
  EngagementPacingType,
  EngagementParticipantRole,
  EngagementsByProjectQuery,
  ExpertPaymentKind,
  ExpertPaymentStatus,
  MyEngagementBucketsQuery,
  MyGovernanceQueueQuery,
  ProjectMemberState,
} from '@/__generated__/graphql';
// Value import: `projectRole` is a behavioural gate, so the enum member is
// compared rather than its wire string.
import { ProjectRole } from '@/__generated__/graphql';

import type { GovernanceQueueRow } from './GovernanceInboxRow';

type LiveEngagement = NonNullable<EngagementHubQuery['engagement']>;
type LiveMoney = { cents: string; currency: string } | null | undefined;

export interface PartyView {
  userId: string;
  name: string;
  /** Their profile picture, for the avatar beside the name; null when unset. */
  pictureUrl: string | null;
  /**
   * The person's role on the project, or null when the api sent none.
   *
   * Carried because "project member" is narrower than "on the project roster".
   * The prototype's `project_member_user_ids` is `members.filter(role ===
   * 'member')` and tracks owners separately in `project_owner_user_ids`, so the
   * Participants card can only label the `member` ones (ENG-5738). Typed as the
   * generated enum rather than `string`, since a behavioural gate now depends
   * on the exact wire value.
   */
  projectRole: ProjectRole | null;
  /**
   * Membership state. An invited (`awaiting_approval`) or `denied` member still
   * arrives with a `user`, so the roster has to exclude them the same way
   * `ProjectPreview` does; otherwise the card presents someone who is not on
   * the project as a participant.
   */
  projectState: ProjectMemberState | null;
}

export interface ProjectView {
  id: string;
  /**
   * `Project.name` is nullable in the schema. Kept null rather than defaulted
   * so the Hub breadcrumb can label an unnamed project `Project` instead of
   * the tautological `Project: Project` (ENG-5737).
   */
  name: string | null;
  /** Absolute project URL from the api; the Header link prefers the id route. */
  htmlUrl: string | null;
  /**
   * How this work is attributed internally. The Hub breadcrumb renders it as a
   * suffix for every viewer who can see the project at all, matching
   * `ProjectBar`, which gates only the team name on `viewer.admin` (ENG-5737).
   */
  trackingCode: string | null;
  /**
   * Customer organization, as the project's group. Only reaches a viewer who
   * holds `project:view` — `primaryProject` is null for the expert — so it is
   * not the expert-side H1's source: that is the ungated
   * `EngagementView.customerOrganizationName` (ENG-5749). Its readers are the
   * staff arm of the Hub breadcrumb's suffix and the customer Billing tab.
   *
   * It ships to every viewer who receives a `primaryProject` at all, and only
   * `projectSuffix` withholds it from non-staff. That is presentation, not
   * gating: the value is in the response and the Apollo cache either way. It
   * is safe here because holding `project:view` means membership in the group
   * being named, so a non-staff viewer could only dig out their own
   * organization's name. The same shape would need a server-side gate
   * wherever `project:view` can be granted independently of membership.
   */
  organizationName: string | null;
  /** Owning group id — the Billing tab's `/team/:groupId/settings/billing` link target. */
  groupId: string | null;
  memberUserIds: string[];
  owners: PartyView[];
}

export interface BillingView {
  /** `BillingClassification.tla`: isSMBAccount ⇒ usd, otherwise credits. */
  isSMBAccount: boolean;
  /** Engagement-scoped config; null when engagements are not enabled. */
  minHours: number | null;
  governanceThresholdCredits: number | null;
  /**
   * The same threshold in dollars, computed by the api at the account's price
   * per credit (ENG-5757). Never derived here: the webapp does not multiply
   * prices (ENG-5605 / ENG-5774). Null when the api could not price it.
   */
  governanceThresholdUsd: { cents: number; currency: string } | null;
  invoiceTermsDays: number | null;
  hoursAutoApproveDays: number | null;
  nteAlertPct: number | null;
  mediationDays: number | null;
  disputeArbitrationDays: number | null;
  reminderDays: number | null;
  autoChargeEnabled: boolean | null;
  defaultBillingEmail: string | null;
}

/**
 * Someone recorded in a channel — they joined it, or they spoke in it.
 *
 * Not the reader set. OF staff read every channel by role and hold no row for
 * it, so an empty roster says nothing about who is watching; a name appears the
 * moment that person writes. `name` is null only when the profile is gone.
 */
export interface ChannelMemberView {
  userId: string;
  joinedAt: string | null;
  name: string | null;
  pictureUrl: string | null;
}

/**
 * `EngagementPersonIdentity` — a party the api names for us, rather than one
 * the client resolves out of the project roster. `name` is `String!` on the
 * wire but api composes it from first/last (`Profile.Name()`), so a nameless
 * profile sends `''`; the mappers below normalise that empty string to null so
 * callers have one null to handle instead of two falsy cases.
 */
export interface PersonIdentityView {
  userId: string;
  name: string | null;
  pictureUrl: string | null;
}

/** A `PersonIdentityView` the mapper has already checked carries a name. */
export type NamedPersonIdentityView = PersonIdentityView & { name: string };

export interface ChannelView {
  id: string;
  channelType: EngagementChannelType;
  lastMessageAt: string | null;
  members: ChannelMemberView[];
}

export interface ActivityView {
  id: string;
  eventType: string;
  actorUserId: string;
  fromState: string | null;
  toState: string | null;
  reason: string | null;
  createdAt: string;
}

export interface HoursSubmissionView {
  id: string;
  expertId: string;
  status: string;
  hoursSubmitted: number;
  hoursConfirmed: number | null;
  workDescription: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  submittedAt: string | null;
  confirmedAt: string | null;
  confirmedByUserId: string | null;
  confirmNote: string | null;
  disputeNote: string | null;
  disputedByUserId: string | null;
  resolution: string | null;
  resolvedAt: string | null;
  resolvedByUserId: string | null;
  approvedAutomatically: boolean;
  /** Expert-side only — null for a customer-side viewer. */
  paidAt: string | null;
}

export interface AmendmentView {
  id: string;
  status: string;
  proposedByUserId: string;
  createdAt: string;
  resolvedAt: string | null;
  /** Expert-side only — null for a customer-side viewer. */
  newExpertRateCents: number | null;
  priorExpertRateCents: number | null;
  /** Customer-side only — null for the expert. */
  newCustomerRateLoadedCents: number | null;
  priorCustomerRateLoadedCents: number | null;
  /** Free-form JSON string on the wire; parsed lazily at the render site. */
  changeSet: string | null;
  /**
   * The pre-amendment values as JSON — **null until the amendment is accepted**,
   * and redacted to the viewer's side like `changeSet`. This is the "Reference
   * (original)" source: the values immediately prior to THIS amendment, not the
   * engagement's original offer.
   */
  priorVersionSnapshot: string | null;
}

/**
 * A governance approver's adjustment or rejection, as the customer side sees it.
 * The api withholds this from the expert entirely, and stops returning it once
 * the engagement is no longer awaiting the customer — the record is
 * stale-by-design after that, so absence is correct rather than lossy.
 */
export interface GovernanceFeedbackView {
  /** `adjustment` or `rejection`. */
  kind: string;
  /** Required on a rejection, optional on an adjustment. */
  note: string | null;
  decidedAt: string | null;
  /** Customer-side money; null on a rejection, which proposes nothing. */
  proposedNteTotalCents: number | null;
  currency: string | null;
}

/**
 * The account's pricing boundaries as the *expert* is allowed to see them:
 * hours, never money. Reaching these through `primaryProject` would have meant
 * reading the customer's billing account, which the api denies to the expert —
 * hence a dedicated expert-side field (ENG-5604 G7).
 */
export interface ExpertOfferContextView {
  engagementMinHours: number;
  reducedThresholdHours: number;
  longTermThresholdHours: number;
}

export interface EngagementView {
  id: string;
  status: string;
  /**
   * Whether the viewer is OnFrontiers staff, from the api's own predicate — the
   * same one the messaging wall uses. Never an authorization answer: the wall is
   * re-derived server-side on every vector, and this only decides what the UI
   * offers.
   */
  viewerIsOfStaff: boolean;
  awaiting: string | null;
  creationPath: EngagementCreationPath | null;
  requiresGovernance: boolean;
  governanceRoleConflated: boolean | null;
  fundingMethod: EngagementFundingMethod | null;

  nteHoursTotal: number | null;
  nteHoursConsumed: number;
  hoursPending: number;

  /** Expert-side money — null for every customer-side viewer. */
  expertRateCents: number | null;
  expertEarningsCents: number | null;
  /** Customer-side money — null for the expert. */
  customerRateLoadedCents: number | null;
  nteTotalCents: number | null;
  /** Currency of whichever money the viewer is entitled to; null when none is. */
  currency: string | null;

  expertId: string | null;
  /**
   * The customer-side user who opened the request — `String!` on the wire and
   * not viewer-gated, so both sides can gate on it. Creator-only actions
   * (accept, withdraw) key off this rather than leaning on the server to reject
   * a click the client should never have offered.
   */
  createdByUserId: string;

  expertOfferMadeAt: string | null;
  customerConfirmedAt: string | null;
  governanceApprovedAt: string | null;
  closingInitiatedAt: string | null;
  cancellationWindowEndsAt: string | null;
  closedAt: string | null;
  closureReason: string | null;

  /** The customer's ask; null until set. */
  requestScopeDescription: string | null;
  /** The expert's scope of work from the offer; null until offered. */
  offerScopeDescription: string | null;
  /** Intended dates — presentational, never enforced against the work. */
  startDate: string | null;
  endDate: string | null;
  /** Null until offered. */
  billingCadence: EngagementBillingCadence | null;
  pacingType: EngagementPacingType | null;
  /** Hours per pacing period; null when unpaced. */
  pacingValue: number | null;
  /** Why the expert declined — **customer side only**, null for the expert. */
  expertDeclineNote: string | null;
  /** **Customer side only**, and only while awaiting the customer. */
  governanceFeedback: GovernanceFeedbackView | null;
  /** **Expert side only** — null for every customer-side viewer. */
  rateNudgeDismissed: boolean | null;
  /**
   * **Expert side only.** Hour thresholds for the §9.11 rate nudge and the
   * minimum-hours check — no money, no take rate. Also null when the account has
   * no engagement settings row.
   */
  expertOfferContext: ExpertOfferContextView | null;

  /** Customer-side roster; **null means denied**, `[]` means genuinely empty. */
  participants: { userId: string; role: EngagementParticipantRole }[] | null;
  messageChannels: ChannelView[];
  project: ProjectView | null;
  billing: BillingView | null;
  activity: ActivityView[];
  hoursSubmissions: HoursSubmissionView[];
  amendments: AmendmentView[];
  hasAcceptedAmendments: boolean;
  /**
   * ENG-5428's server predicate gating the Billing tab: true once a confirming
   * hours submission or a non-skipped billing period exists. Monotone — the
   * tab never disappears once shown, and it is hidden (not empty) before then.
   */
  hasBillingActivity: boolean;
  /**
   * The api's answer to "may this viewer close it right now" — ACTIVE-only and
   * grant-derived (ENG-5688). The Settings close control gates on this rather
   * than on a client-side role, so it cannot offer an action the mutation
   * refuses. Customer-side only: false for OF staff, who close from their own
   * surfaces.
   */
  viewerCanClose: boolean;

  /**
   * Display names for the ids referenced above. Live data can only name users
   * the viewer can already see (project members); unknown ids render a neutral
   * label rather than an id.
   */
  parties: PartyView[];

  /**
   * The expert's own display identity, from `Engagement.expertIdentity` — the
   * one name `parties` structurally cannot carry, since the expert is never a
   * project member. The Hub heading and the Participants roster both read it,
   * so before ENG-5730 the detail page named the expert nowhere at all.
   *
   * Null on two distinct paths, and the neutral labels cover both: no expert
   * attached yet, and no identity resolvable for the one that is.
   */
  expertIdentity: PersonIdentityView | null;

  /**
   * Display-only project name (ENG-5612 B1), visible to every engagement
   * party. `project` above is `project:view`-filtered and resolves null for the
   * expert, who holds neither a team nor a project membership — this is what
   * the eyebrow falls back to so their header names the project rather than
   * reading the literal word "Project".
   */
  projectName: string | null;

  /**
   * Display identities of the customer-side parties — creator, approvers and
   * watchers, no roles — from `Engagement.customerIdentities`. The name table
   * for anyone on the customer side whom `parties` cannot name: for the expert
   * that is everyone, since `primaryProject`, `participants` and a
   * customer-free channel roster leave them nothing (ENG-5749); for the
   * customer side it is an approver who is not a project member (ENG-5750).
   * It reaches the expert only when the customer's disclosure allows, so an
   * empty list is ordinary and every reader falls back to a neutral label.
   * Nameless profiles are dropped here, so every entry carries a name.
   */
  customerIdentities: NamedPersonIdentityView[];

  /**
   * The customer organization's display name — the expert-side Hub heading and
   * identity tile (ENG-5749). Null when unresolvable or when the customer's
   * disclosure withholds it, and the heading then keeps its neutral
   * "Engagement" with no tile.
   */
  customerOrganizationName: string | null;
}

/**
 * Exported so the governance queue parses the wire the same way the Hub does —
 * one implementation of "string cents to a number, null when denied".
 */
export function moneyCents(money: LiveMoney): number | null {
  if (!money) return null;
  const cents = Number(money.cents);
  return Number.isFinite(cents) ? cents : null;
}

function thresholdUsd(money: LiveMoney): BillingView['governanceThresholdUsd'] {
  const cents = moneyCents(money);
  return cents === null || !money ? null : { cents, currency: money.currency };
}

export function decimal(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** `nteHoursConsumed` / `hoursPending` are non-null on the wire; treat a bad parse as 0. */
function requiredDecimal(value: string): number {
  return decimal(value) ?? 0;
}

function timestamp(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function personName(user: {
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}): string {
  if (user.name) return user.name;
  const composed = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return composed || 'Unknown user';
}

/**
 * `EngagementPersonIdentity` → `PersonIdentityView`, with api's empty-string
 * name flattened to null. `||`, not `??`, for exactly that reason: `''` would
 * otherwise render as a blank where a neutral label belongs.
 */
function personIdentity(
  identity: { userId: string; name: string; pictureUrl?: string | null } | null | undefined
): PersonIdentityView | null {
  if (!identity) return null;
  return {
    userId: identity.userId,
    name: identity.name?.trim() || null,
    pictureUrl: identity.pictureUrl ?? null,
  };
}

export function fromLiveEngagement(engagement: LiveEngagement): EngagementView {
  const project = engagement.primaryProject;
  const members = project?.members ?? [];
  const parties: PartyView[] = members
    .map((m): PartyView | null =>
      m.user
        ? {
            userId: m.user.id,
            name: personName(m.user),
            pictureUrl: m.user.picture_url ?? null,
            projectRole: m.role ?? null,
            projectState: m.state ?? null,
          }
        : null
    )
    .filter((p): p is PartyView => p !== null);
  const settings = project?.group?.billing_account?.engagementBillingSettings ?? null;
  const account = project?.group?.billing_account ?? null;

  // The viewer is entitled to at most one side's money; take the currency from
  // whichever side answered rather than assuming USD.
  const currency =
    engagement.expertRate?.currency ??
    engagement.expertEarnings?.currency ??
    engagement.nteTotal?.currency ??
    engagement.customerRateLoaded?.currency ??
    null;

  return {
    id: engagement.id,
    status: engagement.status,
    viewerIsOfStaff: engagement.viewerIsOfStaff,
    awaiting: engagement.awaiting ?? null,
    creationPath: engagement.creationPath,
    requiresGovernance: engagement.requiresGovernance,
    governanceRoleConflated: engagement.governanceRoleConflated ?? null,
    fundingMethod: engagement.fundingMethod ?? null,

    nteHoursTotal: decimal(engagement.nteHoursTotal),
    nteHoursConsumed: requiredDecimal(engagement.nteHoursConsumed),
    hoursPending: requiredDecimal(engagement.hoursPending),

    expertRateCents: moneyCents(engagement.expertRate),
    expertEarningsCents: moneyCents(engagement.expertEarnings),
    customerRateLoadedCents: moneyCents(engagement.customerRateLoaded),
    nteTotalCents: moneyCents(engagement.nteTotal),
    currency,

    expertId: engagement.expertId ?? null,
    createdByUserId: engagement.createdByUserId,

    expertOfferMadeAt: timestamp(engagement.expertOfferMadeAt),
    customerConfirmedAt: timestamp(engagement.customerConfirmedAt),
    governanceApprovedAt: timestamp(engagement.governanceApprovedAt),
    closingInitiatedAt: timestamp(engagement.closingInitiatedAt),
    cancellationWindowEndsAt: timestamp(engagement.cancellationWindowEndsAt),
    closedAt: timestamp(engagement.closedAt),
    closureReason: engagement.closureReason ?? null,

    requestScopeDescription: engagement.requestScopeDescription ?? null,
    offerScopeDescription: engagement.offerScopeDescription ?? null,
    startDate: timestamp(engagement.startDate),
    endDate: timestamp(engagement.endDate),
    billingCadence: engagement.billingCadence ?? null,
    pacingType: engagement.pacingType ?? null,
    pacingValue: decimal(engagement.pacingValue),
    expertDeclineNote: engagement.expertDeclineNote ?? null,
    rateNudgeDismissed: engagement.rateNudgeDismissed ?? null,
    expertOfferContext: engagement.expertOfferContext
      ? {
          engagementMinHours: engagement.expertOfferContext.engagementMinHours,
          reducedThresholdHours: engagement.expertOfferContext.reducedThresholdHours,
          longTermThresholdHours: engagement.expertOfferContext.longTermThresholdHours,
        }
      : null,
    governanceFeedback: engagement.governanceFeedback
      ? {
          kind: engagement.governanceFeedback.kind,
          note: engagement.governanceFeedback.note ?? null,
          decidedAt: timestamp(engagement.governanceFeedback.decidedAt),
          proposedNteTotalCents: moneyCents(engagement.governanceFeedback.proposedNteTotal),
          currency: engagement.governanceFeedback.proposedNteTotal?.currency ?? null,
        }
      : null,

    participants: engagement.participants
      ? engagement.participants.map((p) => ({ userId: p.userId, role: p.role }))
      : null,
    messageChannels: (engagement.messageChannels ?? []).map((c) => ({
      id: c.id,
      channelType: c.channelType,
      lastMessageAt: timestamp(c.lastMessageAt),
      members: c.members.map((m) => ({
        userId: m.userId,
        joinedAt: timestamp(m.joinedAt),
        name: m.identity?.name ?? null,
        pictureUrl: m.identity?.pictureUrl ?? null,
      })),
    })),
    project: project
      ? {
          id: project.id,
          name: project.name ?? null,
          htmlUrl: project.html_url ?? null,
          trackingCode: project.tracking_code ?? null,
          organizationName: project.group?.name ?? null,
          groupId: project.group?.id ?? null,
          memberUserIds: members.map((m) => m.user?.id).filter((id): id is string => Boolean(id)),
          owners: members
            .filter((m) => m.role === 'owner' && m.user)
            .map((m) => ({
              userId: m.user!.id,
              name: personName(m.user!),
              pictureUrl: m.user!.picture_url ?? null,
              projectRole: ProjectRole.Owner,
              projectState: m.state ?? null,
            })),
        }
      : null,
    billing: account
      ? {
          isSMBAccount: account.isSMBAccount,
          minHours: settings?.engagementMinHours ?? null,
          governanceThresholdCredits: settings?.engagementGovernanceThresholdCredits ?? null,
          governanceThresholdUsd: thresholdUsd(settings?.engagementGovernanceThresholdUsd),
          invoiceTermsDays: settings?.invoiceTermsDays ?? null,
          hoursAutoApproveDays: settings?.hoursAutoApproveDays ?? null,
          nteAlertPct: settings?.nteAlertPct ?? null,
          mediationDays: settings?.mediationDays ?? null,
          disputeArbitrationDays: settings?.disputeArbitrationDays ?? null,
          reminderDays: settings?.reminderDays ?? null,
          autoChargeEnabled: settings?.autoChargeEnabled ?? null,
          defaultBillingEmail: settings?.defaultBillingEmail ?? null,
        }
      : null,
    activity: engagement.activity.edges.map((e) => ({
      id: e.node.id,
      eventType: e.node.eventType,
      actorUserId: e.node.actorUserId,
      fromState: e.node.fromState ?? null,
      toState: e.node.toState ?? null,
      reason: e.node.reason ?? null,
      createdAt: String(e.node.createdAt),
    })),
    hoursSubmissions: engagement.hoursSubmissions.map((h) => ({
      id: h.id,
      expertId: h.expertId,
      status: h.status,
      hoursSubmitted: requiredDecimal(h.hoursSubmitted),
      hoursConfirmed: decimal(h.hoursConfirmed),
      workDescription: h.workDescription ?? null,
      periodStart: timestamp(h.periodStart),
      periodEnd: timestamp(h.periodEnd),
      submittedAt: timestamp(h.submittedAt),
      confirmedAt: timestamp(h.confirmedAt),
      confirmedByUserId: h.confirmedByUserId ?? null,
      confirmNote: h.confirmNote ?? null,
      disputeNote: h.disputeNote ?? null,
      disputedByUserId: h.disputedByUserId ?? null,
      resolution: h.resolution ?? null,
      resolvedAt: timestamp(h.resolvedAt),
      resolvedByUserId: h.resolvedByUserId ?? null,
      approvedAutomatically: h.approvedAutomatically,
      paidAt: timestamp(h.paidAt),
    })),
    amendments: engagement.amendments.map((a) => ({
      id: a.id,
      status: a.status,
      proposedByUserId: a.proposedByUserId,
      createdAt: String(a.createdAt),
      resolvedAt: timestamp(a.resolvedAt),
      newExpertRateCents: moneyCents(a.newExpertRate),
      priorExpertRateCents: moneyCents(a.priorExpertRate),
      newCustomerRateLoadedCents: moneyCents(a.newCustomerRateLoaded),
      priorCustomerRateLoadedCents: moneyCents(a.priorCustomerRateLoaded),
      changeSet: a.changeSet ?? null,
      priorVersionSnapshot: a.priorVersionSnapshot ?? null,
    })),
    hasAcceptedAmendments: engagement.hasAcceptedAmendments,
    hasBillingActivity: engagement.hasBillingActivity,
    viewerCanClose: engagement.viewerCanClose,
    parties,
    expertIdentity: personIdentity(engagement.expertIdentity),
    projectName: engagement.primaryProjectName ?? null,
    customerIdentities: engagement.customerIdentities
      .map(personIdentity)
      .filter((p): p is NamedPersonIdentityView => p !== null && p.name !== null),
    customerOrganizationName: engagement.customerOrganizationName?.trim() || null,
  };
}

export interface EngagementListRow {
  engagementId: string;
  projectName: string | null;
  status: string;
  awaiting: string | null;
  nteHoursTotal: number | null;
  nteHoursConsumed: number;
  nextAction: EngagementNextAction;
  hasPendingAction: boolean;
  /**
   * The row's expert, so the page can tell which side the viewer is on for
   * THIS engagement (ENG-5609) — the list is no longer expert-only, and the
   * viewer can be the expert on one row and the customer on the next. Null
   * before an expert is resolved.
   */
  expertId: string | null;
  /**
   * The row's expert by name (ENG-5730). `expertId` above answers which side
   * the viewer is on; this answers who the engagement is *with*, which the
   * card could not say before — the project name alone does not distinguish
   * two engagements on one project.
   */
  expertName: string | null;
}

export interface EngagementBucketsView {
  actionNeeded: EngagementListRow[];
  active: EngagementListRow[];
  closed: EngagementListRow[];
}

type LiveBucketRow = MyEngagementBucketsQuery['myEngagementBuckets']['active'][number];

function listRow(row: LiveBucketRow): EngagementListRow {
  return {
    engagementId: row.id,
    // `primaryProjectName` is the ENG-5612 display-only field: `primaryProject`
    // is `project:view`-filtered and null for the expert this list belongs to.
    projectName: row.primaryProjectName ?? null,
    status: row.status,
    awaiting: row.awaiting ?? null,
    nteHoursTotal: decimal(row.nteHoursTotal),
    nteHoursConsumed: requiredDecimal(row.nteHoursConsumed),
    nextAction: row.nextAction,
    hasPendingAction: row.hasPendingAction,
    expertId: row.expertId ?? null,
    expertName: row.expertIdentity?.name?.trim() || null,
  };
}

/** `myEngagementBuckets` → the three list sections, order preserved. */
export function fromEngagementBuckets(
  buckets: MyEngagementBucketsQuery['myEngagementBuckets']
): EngagementBucketsView {
  return {
    actionNeeded: buckets.actionNeeded.map(listRow),
    active: buckets.active.map(listRow),
    closed: buckets.closed.map(listRow),
  };
}

/**
 * `myGovernanceQueue` rows → the inbox's row shape.
 *
 * The same money rule applies: `nteTotalCents` stays null when the resolver
 * denied it, and the row renders nothing rather than a `$0.00`. An approver is
 * customer-side, so in practice it is populated — but the mapper does not
 * assume that, because the null is the whole point.
 *
 * Every identity field here is nullable for a real reason rather than defensive
 * habit: `expertName` is null on an expertless engagement, `creatorName` when
 * the creator has no resolvable profile, `scope` when the customer skipped it
 * and no offer scope replaced it, `accountName` when the viewer cannot see the
 * project (`FindProjectsByIDs` is `project:view`-filtered and omits rather than
 * errors), and `queuedAt` on a row that reached the queue without a recorded
 * confirmation. The row names the state in each case, or drops the part.
 */
export function fromGovernanceQueue(
  rows: MyGovernanceQueueQuery['myGovernanceQueue']
): GovernanceQueueRow[] {
  return rows.map((row) => ({
    engagementId: row.id,
    projectName: row.primaryProjectName ?? null,
    accountName: row.primaryProject?.group?.name?.trim() || null,
    expertName: row.expertIdentity?.name?.trim() || null,
    expertPictureUrl: row.expertIdentity?.pictureUrl ?? null,
    creatorName: row.createdByIdentity?.name?.trim() || null,
    // The offer scope wins where both exist. Every row in this queue is
    // post-offer by construction (pending_confirmation, awaiting governance),
    // so the expert's scope is the one the approver is authorizing spend
    // against; the customer's request scope is what it started as. The
    // prototype coalesces the same way round (`InboxRow.tsx`).
    scope: row.offerScopeDescription?.trim() || row.requestScopeDescription?.trim() || null,
    // The queue-age clock starts at customer confirmation, not creation:
    // governance is only notified once the customer accepts (spec §7), so
    // anything earlier would age a row that was not yet anyone's to decide.
    queuedAt: timestamp(row.customerConfirmedAt),
    nteHoursTotal: decimal(row.nteHoursTotal),
    nteTotalCents: moneyCents(row.nteTotal),
    currency: row.nteTotal?.currency ?? null,
  }));
}

export interface ProjectEngagementRow {
  engagementId: string;
  status: string;
  awaiting: string | null;
  /** The expert's user id, or null before one is attached. */
  expertId: string | null;
  /**
   * The expert's display name from ENG-5612 B3's `expertIdentity` — the expert
   * is not a project member, so `parties.ts` cannot resolve it client-side.
   * Resolved live off `expert_id` rather than stored per row, so the nulls are
   * the two the neutral labels cover: no expert attached yet, and no identity
   * resolvable for the one that is.
   */
  expertName: string | null;
  /**
   * The customer's free-text scope from creation; null when they skipped it
   * (`HireExpertDrawer` does not require it). This is the row's title —
   * see `ProjectEngagementsSection`.
   */
  requestScope: string | null;
  nteHoursTotal: number | null;
  nteHoursConsumed: number;
  updatedAt: string;
}

/** `engagementsByProject` edges → the project section's rows. */
export function fromProjectEngagements(
  page: EngagementsByProjectQuery['engagementsByProject']
): ProjectEngagementRow[] {
  return page.edges.map((edge) => ({
    engagementId: edge.node.id,
    status: edge.node.status,
    awaiting: edge.node.awaiting ?? null,
    expertId: edge.node.expertId ?? null,
    // `||`, not `??`: `name` is `String!` on the wire but api composes it from
    // first/last (`Profile.Name()`), so a nameless profile sends `''` — which
    // would render a dangling `' · 40h of 100h'` instead of the neutral label.
    expertName: edge.node.expertIdentity?.name?.trim() || null,
    requestScope: edge.node.requestScopeDescription ?? null,
    nteHoursTotal: decimal(edge.node.nteHoursTotal),
    nteHoursConsumed: requiredDecimal(edge.node.nteHoursConsumed),
    updatedAt: String(edge.node.updatedAt),
  }));
}

/*
 * ── Billing tab (FE4 / ENG-5450, api ENG-5428) ──────────────────────────────
 *
 * The wire is structurally segregated: `billing` resolves the viewer's side
 * once and answers `customer` XOR `expert` as whole nullable objects, so these
 * views never merge the two arms and nothing here re-derives the side. The
 * money rules at the top of this file apply unchanged, with one deliberate
 * nuance on each arm:
 *   - customer `creditBalanceCents` null means USD arm OR the viewer lacks
 *     `billing:view` on the funding account — render nothing, never "$0".
 *   - expert `totalEarnedCents` null means "nothing paid yet" (`gql.Money`
 *     cannot express a zero) or an XP-off environment. The panel owns how to
 *     say that; the mapper does not coerce it to 0.
 */

export interface BillingPeriodRow {
  id: string;
  periodStart: string | null;
  periodEnd: string | null;
  /** Stamped at CLAIM time — "when we billed", never "when it was paid". */
  closedAt: string | null;
  status: EngagementBillingPeriodStatus;
  /** This row's own funding path, frozen at close — the interleave key. */
  fundingMethod: EngagementFundingMethod;
  hoursConfirmed: number;
  /** USD cost; ZERO (not null) on a skipped period. Rendered on USD rows. */
  amountCents: number | null;
  amountCurrency: string | null;
  /** OFC draw; genuinely null on a USD-settled row. Rendered on credits rows. */
  creditAmountCents: number | null;
  creditAmountCurrency: string | null;
  /**
   * One bit off `stripeInvoiceId`: a PENDING USD row WITH an invoice is
   * "invoiced, awaiting payment" (normal for up to net-X days); WITHOUT one is
   * "not yet invoiced" (transient). An id is not a link — see
   * `hostedInvoiceUrl` for that.
   */
  invoiced: boolean;
  /**
   * Stripe's hosted invoice page — the receipt link, and a bearer capability,
   * so the api gates it on `billing:view` rather than on viewer side. Null on
   * a credits row, on a USD row whose invoice is not yet ISSUED (the id is
   * stamped at draft, the URL only at issue), and for a customer-side viewer
   * without `billing:view` on the funding account. Those last two are NOT
   * separable from the row, and both mean the same thing to the UI — no
   * receipt to offer this viewer — so render on presence, never on `invoiced`.
   */
  hostedInvoiceUrl: string | null;
  /** When the money LANDED. Never a `closedAt` synonym — that is claim time. */
  settledAt: string | null;
  /**
   * Server vocabulary, deliberately an open string: render the known reasons
   * with copy and any unknown one generically, never crash on a new value.
   */
  failureReason: string | null;
}

export interface BracketPositionView {
  index: number;
  cumulativeHours: number | null;
  /** Null in the final unbounded bracket. */
  hoursToNext: number | null;
  finalBracket: boolean;
}

export interface BillingCustomerArmView {
  fundingMethod: EngagementFundingMethod;
  chargeMechanism: string | null;
  creditBalanceCents: number | null;
  creditBalanceCurrency: string | null;
  hoursConsumed: number;
  hoursRemaining: number;
  bracketPosition: BracketPositionView | null;
  /** Oldest first, as served — both funding modes in one chronological list. */
  periods: BillingPeriodRow[];
}

export interface ExpertPaymentRow {
  id: string;
  amountCents: number | null;
  currency: string | null;
  status: ExpertPaymentStatus;
  kind: ExpertPaymentKind;
  paidAt: string | null;
  hoursSubmissionId: string;
}

export interface BillingExpertArmView {
  totalEarnedCents: number | null;
  maxEarningsCents: number | null;
  currency: string | null;
  /**
   * Tri-state: true / false / null. Null is UNKNOWN — the api's live Stripe
   * read degraded on an outage — and must never collapse to false.
   */
  connectPayoutsEnabled: boolean | null;
  /**
   * Newest first, as served. Empty on an XP-off environment, which the wire
   * cannot distinguish from "never paid" — copy must not assert the latter.
   */
  payments: ExpertPaymentRow[];
}

export interface BillingTabView {
  viewerSide: EngagementBillingViewerSide;
  customer: BillingCustomerArmView | null;
  expert: BillingExpertArmView | null;
}

type LiveBilling = NonNullable<NonNullable<EngagementBillingQuery['engagement']>['billing']>;

/** `engagement.billing` → the tab's view. Null billing (no activity, or a non-participant) never reaches this. */
export function fromEngagementBilling(billing: LiveBilling): BillingTabView {
  const customer = billing.customer ?? null;
  const expert = billing.expert ?? null;
  return {
    viewerSide: billing.viewerSide,
    customer: customer && {
      fundingMethod: customer.fundingMethod,
      chargeMechanism: customer.chargeMechanism ?? null,
      creditBalanceCents: moneyCents(customer.creditBalance),
      creditBalanceCurrency: customer.creditBalance?.currency ?? null,
      hoursConsumed: requiredDecimal(customer.hoursConsumed),
      hoursRemaining: requiredDecimal(customer.hoursRemaining),
      bracketPosition: customer.bracketPosition
        ? {
            index: customer.bracketPosition.index,
            cumulativeHours: decimal(customer.bracketPosition.cumulativeHours),
            hoursToNext: decimal(customer.bracketPosition.hoursToNext),
            finalBracket: customer.bracketPosition.finalBracket,
          }
        : null,
      periods: customer.periods.map((p) => ({
        id: p.id,
        periodStart: timestamp(p.periodStart),
        periodEnd: timestamp(p.periodEnd),
        closedAt: timestamp(p.closedAt),
        status: p.status,
        fundingMethod: p.fundingMethod,
        hoursConfirmed: requiredDecimal(p.hoursConfirmed),
        amountCents: moneyCents(p.amount),
        amountCurrency: p.amount?.currency ?? null,
        creditAmountCents: moneyCents(p.creditAmount),
        creditAmountCurrency: p.creditAmount?.currency ?? null,
        invoiced: Boolean(p.stripeInvoiceId),
        hostedInvoiceUrl: p.hostedInvoiceUrl ?? null,
        settledAt: timestamp(p.settledAt),
        failureReason: p.failureReason ?? null,
      })),
    },
    expert: expert && {
      totalEarnedCents: moneyCents(expert.totalEarned),
      maxEarningsCents: moneyCents(expert.maxEarnings),
      currency: expert.totalEarned?.currency ?? expert.maxEarnings?.currency ?? null,
      connectPayoutsEnabled: expert.connectPayoutsEnabled ?? null,
      payments: expert.payments.map((payment) => ({
        id: payment.id,
        amountCents: moneyCents(payment.amount),
        currency: payment.amount?.currency ?? null,
        status: payment.status,
        kind: payment.kind,
        paidAt: timestamp(payment.paidAt),
        hoursSubmissionId: payment.hoursSubmissionId,
      })),
    },
  };
}
